import { styled } from '@linaria/react';
import type { AgentMinerSessionLinkedTask } from '../../../api/agent-miner-client/agent-miner-client.type';
import { inkLight, skyDark, skyLightest } from '../../../styles/design-tokens';
import { Tooltip } from '../../../ui-library/tooltip/tooltip';
import { Text } from '../../../ui-library/typography/typography';
import { formatTaskLine } from './shared.helpers';
import { TaskChip } from './task-chip';

type TasksChipListProps = {
  tasks: AgentMinerSessionLinkedTask[];
  size?: 'sm' | 'md' | 'lg';
  wrap?: boolean;
  showFullLabel?: boolean;
  onTaskClick?: (task: AgentMinerSessionLinkedTask) => void;
};

export function TasksChipList({
  tasks,
  size = 'sm',
  wrap = false,
  showFullLabel = false,
  onTaskClick,
}: TasksChipListProps) {
  if (!tasks || tasks.length === 0) return <Dash>—</Dash>;

  const [first, ...rest] = tasks;

  if (rest.length === 0) {
    return (
      <Wrapper wrap={wrap}>
        <TaskChip task={first} size={size} showFullLabel={showFullLabel} onClick={onTaskClick} />
      </Wrapper>
    );
  }

  const overflowLabel = rest.map(formatTaskLine).join('\n');

  return (
    <Wrapper wrap={wrap}>
      <TaskChip key={first.id} task={first} size={size} showFullLabel={showFullLabel} onClick={onTaskClick} />
      {wrap ? (
        rest.map((task) => (
          <TaskChip key={task.id} task={task} size={size} showFullLabel={showFullLabel} onClick={onTaskClick} />
        ))
      ) : (
        <Tooltip label={overflowLabel} variant="black" multiline style={{ maxWidth: 280, whiteSpace: 'pre-line' }}>
          <Overflow>+{rest.length}</Overflow>
        </Tooltip>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.span<{ wrap: boolean }>`
  display: inline-flex;
  flex-wrap: ${(props) => (props.wrap ? 'wrap' : 'nowrap')};
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Overflow = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  background: ${skyLightest};
  color: ${inkLight};
  font: 500 11px/1.2 'Figtree', sans-serif;
  border-radius: 16px;
  cursor: default;
`;

const Dash = styled(Text)`
  color: ${skyDark};
  font-size: 12px;
`;
